import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { BookButton } from "@/components/BookButton";

const WORDS = ["data entry", "lead follow-up", "weekly reporting", "invoice chasing", "copy-paste"];
const LONGEST = "weekly reporting.";
const HOLD_MS = 7000;
const STRIKE_MS = 500;

// Cycling word gets its own dedicated line so the headline block height is
// constant and nothing below ever shifts. Only transform and opacity animate
// (GPU composited) so it stays smooth on mobile. The wrap from the last word
// back to the first uses the identical transition, so the loop is seamless.
function CyclingWord() {
    const reduce = useReducedMotion();
    const [i, setI] = useState(0);
    const [striking, setStriking] = useState(false);

    useEffect(() => {
        if (reduce) {
            const id = setInterval(() => {
                if (document.hidden) return;
                setI((p) => (p + 1) % WORDS.length);
            }, HOLD_MS);
            return () => clearInterval(id);
        }
        const id = setInterval(() => {
            if (document.hidden) return;
            setStriking(true);
            setTimeout(() => {
                setI((p) => (p + 1) % WORDS.length);
                setStriking(false);
            }, STRIKE_MS);
        }, HOLD_MS);
        return () => clearInterval(id);
    }, [reduce]);

    return (
        <span className="relative block" style={{ height: "1.2em" }}>
            <AnimatePresence mode="wait" initial={false}>
                <motion.span
                    key={i}
                    data-testid="hero-cycling-word"
                    className="absolute left-0 top-0 inline-flex items-baseline whitespace-nowrap"
                    initial={reduce ? { opacity: 0 } : { opacity: 0, y: "0.28em" }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={reduce ? { opacity: 0 } : { opacity: 0, y: "-0.24em" }}
                    transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                >
                    <span className="relative inline-block">
                        {WORDS[i]}
                        {!reduce && (
                            <motion.span
                                aria-hidden
                                className="absolute left-[-0.04em] right-[-0.04em] top-1/2 h-[0.07em] origin-left rounded-full bg-current"
                                initial={{ scaleX: 0, opacity: 0 }}
                                animate={{ scaleX: striking ? 1 : 0, opacity: striking ? 1 : 0 }}
                                transition={striking ? { duration: 0.45, ease: [0.65, 0, 0.35, 1] } : { duration: 0 }}
                                style={{ translateY: "-50%" }}
                            />
                        )}
                    </span>
                    <span className="period">.</span>
                </motion.span>
            </AnimatePresence>
        </span>
    );
}

export function Hero() {
    const h1Ref = useRef(null);
    const measureRef = useRef(null);
    const [fontSize, setFontSize] = useState(null);

    // fit the whole headline so the LONGEST word plus its period always fits
    // the content column at every width, with comfortable margin.
    useLayoutEffect(() => {
        const fit = () => {
            if (!h1Ref.current || !measureRef.current) return;
            const avail = h1Ref.current.clientWidth;
            const refWidth = measureRef.current.getBoundingClientRect().width;
            const refSize = 200;
            if (!refWidth) return;
            const px = Math.min(112, (avail * 0.94) / refWidth * refSize);
            setFontSize(px);
        };
        fit();
        if (document.fonts && document.fonts.ready) document.fonts.ready.then(fit);
        window.addEventListener("resize", fit);
        window.addEventListener("load", fit);
        return () => {
            window.removeEventListener("resize", fit);
            window.removeEventListener("load", fit);
        };
    }, []);

    return (
        <section id="top" data-testid="hero" className="relative overflow-hidden pt-32 pb-16 sm:pt-40 sm:pb-24">
            {/* ambient paper grain texture */}
            <div className="pointer-events-none absolute inset-0 paper-grain opacity-[0.5]" />
            <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 h-[520px] w-[820px] rounded-full bg-surface/60 blur-3xl" />

            {/* hidden reference for fit-to-width measurement */}
            <span
                ref={measureRef}
                aria-hidden
                className="font-display font-bold tracking-[-0.03em]"
                style={{ position: "absolute", visibility: "hidden", whiteSpace: "nowrap", fontSize: "200px", left: -9999, top: -9999 }}
            >
                {LONGEST}
            </span>

            <div className="relative max-w-5xl mx-auto px-5 sm:px-8">
                <motion.p
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="eyebrow mb-8"
                >
                    Fractional AI teams
                </motion.p>

                <motion.h1
                    ref={h1Ref}
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.05 }}
                    className="font-display font-bold tracking-[-0.03em] leading-[1.05] text-[clamp(2rem,9vw,7rem)]"
                    style={fontSize ? { fontSize: `${fontSize}px` } : undefined}
                >
                    <span className="block">Replace the</span>
                    <CyclingWord />
                </motion.h1>

                <motion.p
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.18 }}
                    className="font-display font-semibold tracking-[-0.02em] text-2xl sm:text-4xl lg:text-5xl mt-6 text-foreground/90"
                >
                    Fractional AI integration teams. For growing businesses<span className="period">.</span>
                </motion.p>

                <motion.p
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.3 }}
                    className="max-w-2xl text-base sm:text-lg text-muted-foreground leading-relaxed mt-8"
                >
                    We are your AI integration team, on retainer. We find where AI pays dividends,
                    where it's a liability, and wire the system into your workflows. Then we maintain
                    it for you. For less than one junior AI engineer's salary.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.4 }}
                    className="mt-10"
                >
                    <BookButton testid="hero-book-a-call" />
                </motion.div>
            </div>
        </section>
    );
}
